import {useMutation, useQueryClient} from '@github-ui/react-query'
import {
  getCurrentRepositoryNwo,
  getNavigationTabCountAuthoritativeVersion,
  getNavigationTabCountSnapshot,
  getNavigationTabCountTargetVersion,
  updateNavigationTabCount,
} from '@github-ui/global-navigation'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'

export function useClosePullRequestMutation({
  onError,
  onSuccess,
}: {
  onError?: (error: Error) => void
  onSuccess?: () => void
} = {}) {
  const apiURL = usePageDataUrl(PageData.closePullRequest)
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  return useMutation({
    onMutate: () => ({
      repositoryNwo: getCurrentRepositoryNwo(),
      tabCount: getNavigationTabCountSnapshot('pull-requests'),
      targetVersion: getNavigationTabCountTargetVersion('pull-requests'),
      updateVersion: getNavigationTabCountAuthoritativeVersion('pull-requests'),
    }),
    mutationFn: async () => {
      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: (_data, _variables, {repositoryNwo, tabCount, targetVersion, updateVersion}) => {
      onSuccess?.()
      updateNavigationTabCount(
        'pull-requests',
        {
          type: 'delta',
          delta: -1,
          ...(tabCount?.exact ? {expectedCountAfterAuthoritativeUpdate: tabCount.count} : {}),
        },
        {
          repositoryNwo,
          expectedUpdateVersion: updateVersion,
          expectedTargetVersion: targetVersion,
        },
      )
      return queryClient.refetchQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
    },
    onError: (e: Error) => onError?.(e),
  })
}
